import type {
  AttachmentRequirement,
  AttachmentType,
  BuyerReview,
  DashboardSummary,
  FormalProductMaster,
  ImportBatchRecord,
  ProductFamilyItem,
  CompetitorComparisonData,
  InviteToken,
  LaunchAnnouncement,
  LaunchProductCandidate,
  ProductCategory,
  ProductMaster,
  ProductMasterWorkItem,
  ProductSubcategory,
  VendorProposal,
  VendorProposalAttachment,
  VendorProposalInvitation,
  QuotationRound,
  Store,
  StoreReview,
  Submission,
  SystemSettings,
  User,
  Vendor,
  VendorApplication,
  VendorAccountSummary,
  VendorChangeRequest,
  VendorMasterAttachment,
  VendorOption,
  VendorProposalVersion,
  VendorSelfServiceProfile,
} from './shared/types';
import { mapProductMasterRow } from './shared/competitor-comparison';

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
    public readonly requestId?: string,
  ) {
    super(message);
  }
}

export function getApiErrorMessage(error: unknown, fallback = '操作失敗，請稍後再試。'): string {
  if (error instanceof ApiError && error.message) {
    if (error.status === 504 || error.status === 502) {
      return '無法連線至後端伺服器 (Port 8787)，請確認本機 API 服務已啟動（npm run dev:api 或 npm run dev:all）。';
    }
    return error.message;
  }
  if (error instanceof Error && error.message) {
    if (
      error.message.includes('Failed to fetch') ||
      error.message.includes('NetworkError') ||
      error.message.includes('Load failed') ||
      error.message.includes('fetch failed')
    ) {
      return '無法連線至後端伺服器，請確認本機後端服務已啟動（npm run dev:all）。';
    }
    if (error.message.includes('Cannot read properties') || error.message.includes('undefined')) {
      return fallback;
    }
    return error.message;
  }
  if (typeof error === 'object' && error !== null) {
    const err = error as any;
    if (typeof err.message === 'string' && err.message) return err.message;
    if (err.data && typeof err.data.message === 'string') return err.data.message;
    if (err.error) {
      if (typeof err.error.message === 'string') return err.error.message;
      if (typeof err.error === 'string') return err.error;
    }
  }
  if (typeof error === 'string' && error) return error;
  return fallback;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(path, { credentials: 'include', ...options });
  const contentType = response.headers.get('Content-Type') ?? '';
  if (!contentType.includes('application/json')) {
    const errorText = await response.text().catch(() => '');
    let friendlyMsg = '伺服器回覆格式不正確。';
    if (response.status === 413) {
      friendlyMsg = '上傳檔案或資料量過大，超出伺服器單次請求限制。';
    } else if (response.status === 503 || response.status === 502 || response.status === 504) {
      friendlyMsg = `伺服器連線逾時或暫時無法處理 (HTTP ${response.status})，請稍後再試。`;
    } else if (response.status === 401) {
      friendlyMsg = '登入狀態已失效，請重新登入。';
    } else if (response.status === 404) {
      friendlyMsg = `找不到指定之服務端點 (HTTP 404: ${path})。`;
    }
    throw new ApiError(
      'INVALID_RESPONSE',
      friendlyMsg,
      response.status,
      errorText ? errorText.slice(0, 500) : undefined,
      response.headers.get('X-Request-Id') ?? undefined,
    );
  }
  const body = (await response.json()) as any;
  if (!response.ok || body?.ok === false || body?.success === false) {
    const errorCode =
      body?.error?.code ||
      body?.code ||
      (typeof body?.error === 'string' ? body.error : 'REQUEST_FAILED');
    const errorMsg =
      body?.error?.message ||
      body?.message ||
      (typeof body?.error === 'string' ? body.error : '請求失敗。');
    const requestId =
      body?.error?.requestId ||
      body?.error?.request_id ||
      body?.requestId ||
      response.headers.get('X-Request-Id') ||
      undefined;
    throw new ApiError(
      errorCode,
      errorMsg,
      response.status,
      body?.error?.details || body?.details,
      requestId,
    );
  }
  const responseData = (body?.data !== undefined ? body.data : body) as T;
  return responseData;
}

function jsonBody(value: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(value),
  };
}

export const api = {
  vendorEntry: {
    resolve: (taxId: string) =>
      request<{
        flow:
          | 'FIRST_TIME_PASSWORD_SETUP'
          | 'PASSWORD_REQUIRED'
          | 'PASSWORD_RESET_REQUIRED'
          | 'LOCKED'
          | 'DISABLED'
          | 'NEW_VENDOR'
          | 'EXISTING_APPLICATION'
          | 'SUSPENDED_VENDOR'
          | 'INACTIVE_VENDOR';
        application_status?: string | null;
        vendor_status?: string | null;
        account_status?: VendorAccountSummary['status'] | null;
        login_account?: string | null;
        setup_token?: string | null;
        setup_expires_at?: string | null;
      }>('/api/vendor-entry/resolve', jsonBody({ tax_id: taxId })),
  },
  auth: {
    me: () => request<{ user: User }>('/api/auth/me'),
    login: (username: string, password: string, taxId?: string) =>
      request<{ user: User }>(
        '/api/auth/login',
        jsonBody({ username, password, ...(taxId ? { tax_id: taxId } : {}) }),
      ),
    logout: () => request<null>('/api/auth/logout', { method: 'POST' }),
  },
  vendorAccount: {
    login: (taxId: string, password: string) =>
      request<{ user: User }>('/api/vendor-account/login', jsonBody({ tax_id: taxId, password })),
    setupPassword: (input: {
      setup_token: string;
      password: string;
      confirm_password: string;
      turnstile_token: string;
    }) => request<{ user: User }>('/api/vendor-auth/setup-password', jsonBody(input)),
    detail: (vendorId: string) =>
      request<{ account: VendorAccountSummary | null }>(`/api/vendors/${vendorId}/account`),
    action: (vendorId: string, action: 'RESET_PASSWORD' | 'UNLOCK' | 'DISABLE' | 'ENABLE') =>
      request<{ account: VendorAccountSummary }>(
        `/api/vendors/${vendorId}/account/action`,
        jsonBody({ action }),
      ),
  },
  dashboard: () => request<{ summary: DashboardSummary }>('/api/dashboard'),
  productMasterWorkItems: {
    list: (params: Record<string, string> = {}) =>
      request<{
        work_items: ProductMasterWorkItem[];
        summary: Record<string, number>;
        filters: Record<string, string>;
      }>(`/api/product-master-work-items?${new URLSearchParams(params)}`),
    detail: (id: string) =>
      request<{ work_item: ProductMasterWorkItem; proposal: VendorProposal }>(
        `/api/product-master-work-items/${id}`,
      ),
    start: (id: string) =>
      request<{ work_item: ProductMasterWorkItem; proposal: VendorProposal }>(
        `/api/product-master-work-items/${id}/start`,
        { method: 'POST' },
      ),
    createProduct: (id: string, input: unknown) =>
      request<{
        productMaster: ProductMaster;
        workItem: ProductMasterWorkItem;
        announcementActions: {
          canCreateAnnouncement: boolean;
          canAddToDraftAnnouncement: boolean;
        };
      }>(`/api/product-master-work-items/${id}/create-product`, jsonBody(input)),
  },
  stores: () => request<{ stores: Store[] }>('/api/stores'),
  vendors: {
    list: (params: Record<string, string> = {}) =>
      request<{ vendors: Vendor[] }>(`/api/vendors?${new URLSearchParams(params)}`),
    detail: (id: string) => request<Vendor>(`/api/vendors/${id}`),
    pdfUrl: (id: string) => `/api/vendors/${id}/pdf`,
    create: (input: unknown) => request<Vendor>('/api/vendors', jsonBody(input)),
    update: (id: string, input: unknown) =>
      request<Vendor>(`/api/vendors/${id}`, { ...jsonBody(input), method: 'PUT' }),
    status: (id: string, vendor_status: string, reason = '') =>
      request<Vendor>(`/api/vendors/${id}/status`, jsonBody({ vendor_status, reason })),
    confirmMigration: (id: string, reason: string) =>
      request<Vendor>(`/api/vendors/${id}/migration-confirm`, jsonBody({ reason })),
    options: () =>
      request<{ settlement_methods: VendorOption[]; payment_terms: VendorOption[] }>(
        '/api/vendor-options',
      ),
    createOption: (input: unknown) => request<VendorOption>('/api/vendor-options', jsonBody(input)),
    updateOption: (id: string, input: unknown) =>
      request<VendorOption>(`/api/vendor-options/${id}`, { ...jsonBody(input), method: 'PUT' }),
  },
  vendorApplications: {
    list: (params: Record<string, string> = {}) =>
      request<{ applications: VendorApplication[] }>(
        `/api/vendor-applications?${new URLSearchParams(params)}`,
      ),
    detail: (id: string) => request<VendorApplication>(`/api/vendor-applications/${id}`),
    pdfUrl: (id: string) => `/api/vendor-applications/${id}/pdf`,
    approve: (id: string, reason = '') =>
      request<{ application: VendorApplication; vendor: Vendor }>(
        `/api/vendor-applications/${id}/approve`,
        jsonBody({ reason }),
      ),
    reject: (id: string, reason: string) =>
      request<VendorApplication>(`/api/vendor-applications/${id}/reject`, jsonBody({ reason })),
    review: (id: string, input: unknown) =>
      request<VendorApplication>(`/api/vendor-applications/${id}/review`, jsonBody(input)),
  },
  rounds: {
    list: () => request<{ rounds: QuotationRound[] }>('/api/quotation-rounds'),
    detail: (id: string) => request<Record<string, unknown>>(`/api/quotation-rounds/${id}`),
    create: (input: unknown) =>
      request<Record<string, unknown>>('/api/quotation-rounds', jsonBody(input)),
    update: (id: string, input: unknown) =>
      request<Record<string, unknown>>(`/api/quotation-rounds/${id}`, {
        ...jsonBody(input),
        method: 'PUT',
      }),
    delete: (id: string) =>
      request<{ deleted: boolean }>(`/api/quotation-rounds/${id}`, { method: 'DELETE' }),
    archive: (id: string, reason = '') =>
      request<{ archived: boolean }>(`/api/quotation-rounds/${id}/archive`, jsonBody({ reason })),
  },
  invitations: {
    list: () => request<{ invitations: InviteToken[] }>('/api/invitations'),
    create: (input: unknown) =>
      request<{ token: string; expires_at: string; invite_url: string }>(
        '/api/invitations',
        jsonBody(input),
      ),
    revoke: (id: string) =>
      request<{ revoked: boolean }>(`/api/invitations/${id}/revoke`, { method: 'POST' }),
  },
  submissions: {
    list: (params: Record<string, string> = {}) =>
      request<{ submissions: Submission[] }>(`/api/submissions?${new URLSearchParams(params)}`),
    detail: (id: string) => request<Submission>(`/api/submissions/${id}`),
    review: (
      id: string,
      input: Partial<BuyerReview> & {
        required_fields_checked: boolean;
        attachments_checked: boolean;
      },
    ) => request<Submission>(`/api/submissions/${id}/review`, jsonBody(input)),
    batchSubcategory: (input: unknown) =>
      request<{ submissions: Submission[] }>('/api/submissions/batch-subcategory', jsonBody(input)),
    requestRevision: (id: string, reason: string, revisionDueAt: string | null = null) =>
      request<Submission>(
        `/api/submissions/${id}/request-revision`,
        jsonBody({ status: 'REVISION_REQUIRED', reason, revision_due_at: revisionDueAt }),
      ),
    returnSubmission: (
      id: string,
      input: { reason: string; item_codes: string[]; revision_due_at?: string | null },
    ) => request<Submission>(`/api/submissions/${id}/reject`, jsonBody(input)),
    reviewTradeTerms: (
      id: string,
      input: { decision: 'ACCEPT' | 'REJECT' | 'REQUEST_REVISION'; reason?: string },
    ) => request<Submission>(`/api/submissions/${id}/trade-terms`, jsonBody(input)),
    storeReview: (
      id: string,
      input: Omit<
        StoreReview,
        'id' | 'submission_id' | 'store_id' | 'reviewer_id' | 'created_at' | 'updated_at'
      >,
    ) => request<Submission>(`/api/submissions/${id}/store-reviews`, jsonBody(input)),
    status: (id: string, status: string, reason = '') =>
      request<Submission>(`/api/submissions/${id}/status`, jsonBody({ status, reason })),
  },
  productSubcategories: {
    publicList: (categoryCode: string) =>
      request<{ subcategories: ProductSubcategory[] }>(
        `/api/public/product-subcategories?category_code=${encodeURIComponent(categoryCode)}`,
      ),
    list: (params: Record<string, string> = {}) =>
      request<{ subcategories: ProductSubcategory[] }>(
        `/api/product-subcategories?${new URLSearchParams(params)}`,
      ),
    create: (input: unknown) =>
      request<{ subcategory: ProductSubcategory }>('/api/product-subcategories', jsonBody(input)),
    update: (id: string, input: unknown) =>
      request<{ subcategory: ProductSubcategory }>(`/api/product-subcategories/${id}`, {
        ...jsonBody(input),
        method: 'PUT',
      }),
  },
  vendorSubmission: {
    get: (token: string) =>
      request<{
        submission: Submission;
        round: {
          id: string;
          name: string;
          category: string;
          description: string;
          due_date: string;
          required_attachment_types: AttachmentType[];
        };
        vendor: Vendor;
      }>(`/api/vendor/submissions?token=${encodeURIComponent(token)}`),
    save: (token: string, product: unknown) =>
      request<Submission>('/api/vendor/submissions', {
        ...jsonBody({ product }),
        headers: { 'Content-Type': 'application/json', 'X-Vendor-Token': token },
        method: 'PUT',
      }),
    submit: (
      token: string,
      submissionId: string,
      turnstileToken: string,
      idempotencyKey?: string,
    ) =>
      request<Submission>(
        `/api/vendor/submissions/${submissionId}?token=${encodeURIComponent(token)}`,
        jsonBody({ turnstile_token: turnstileToken, idempotency_key: idempotencyKey }),
      ),
  },
  attachments: {
    upload: async (input: { token?: string; submissionId: string; type: string; file: File }) => {
      const form = new FormData();
      form.set('submission_id', input.submissionId);
      form.set('type', input.type);
      form.set('file', input.file);
      return request<unknown>(
        `/api/attachments${input.token ? `?token=${encodeURIComponent(input.token)}` : ''}`,
        {
          method: 'POST',
          body: form,
          headers: input.token ? { 'X-Vendor-Token': input.token } : undefined,
        },
      );
    },
    downloadUrl: (id: string, token?: string) =>
      `/api/attachments/${id}${token ? `?token=${encodeURIComponent(token)}` : ''}`,
  },
  vendorAttachments: {
    upload: async (vendorId: string, type: string, file: File) => {
      const form = new FormData();
      form.set('type', type);
      form.set('file', file);
      return request<VendorMasterAttachment>(`/api/vendors/${vendorId}/attachments`, {
        method: 'POST',
        body: form,
      });
    },
    list: (vendorId: string) =>
      request<{ attachments: VendorMasterAttachment[] }>(`/api/vendors/${vendorId}/attachments`),
    downloadUrl: (vendorId: string, attachmentId: string) =>
      `/api/vendors/${vendorId}/attachments/${attachmentId}`,
  },
  meeting: {
    list: () => request<{ decisions: Record<string, unknown>[] }>('/api/meeting-decisions'),
    decide: (input: unknown) => request<Submission>('/api/meeting-decisions', jsonBody(input)),
  },
  reports: {
    list: (params: Record<string, string> = {}) =>
      request<{
        rows: Record<string, unknown>[];
        filters: Record<string, string>;
        generated_at: string;
      }>(`/api/reports?${new URLSearchParams(params)}`),
    csvUrl: (params: Record<string, string> = {}) =>
      `/api/reports?${new URLSearchParams({ ...params, format: 'csv' })}`,
  },
  launchAnnouncements: {
    list: () => request<{ announcements: LaunchAnnouncement[] }>('/api/launch-announcements'),
    candidates: () =>
      request<{ candidates: LaunchProductCandidate[] }>('/api/launch-announcements/candidates'),
    detail: (id: string) =>
      request<{ announcement: LaunchAnnouncement }>(`/api/launch-announcements/${id}`),
    create: (input: unknown) =>
      request<{ announcement: LaunchAnnouncement }>('/api/launch-announcements', jsonBody(input)),
    update: (id: string, input: unknown) =>
      request<{ announcement: LaunchAnnouncement }>(`/api/launch-announcements/${id}`, {
        ...jsonBody(input),
        method: 'PUT',
      }),
    addItem: (id: string, input: unknown) =>
      request<{ announcement: LaunchAnnouncement }>(
        `/api/launch-announcements/${id}/items`,
        jsonBody(input),
      ),
    publish: (id: string) =>
      request<{ announcement: LaunchAnnouncement }>(`/api/launch-announcements/${id}/publish`, {
        method: 'POST',
      }),
    withdraw: (id: string, reason: string) =>
      request<{ announcement: LaunchAnnouncement }>(
        `/api/launch-announcements/${id}/withdraw`,
        jsonBody({ reason }),
      ),
    revise: (id: string) =>
      request<{ announcement: LaunchAnnouncement }>(`/api/launch-announcements/${id}/revise`, {
        method: 'POST',
      }),
    markRead: (id: string) =>
      request<{ announcement_id: string; read_at: string }>(
        `/api/launch-announcements/${id}/read`,
        { method: 'POST' },
      ),
  },
  settings: {
    get: () => request<{ settings: SystemSettings }>('/api/system-settings'),
    update: (settings: SystemSettings) =>
      request<{ settings: SystemSettings }>('/api/system-settings', {
        ...jsonBody(settings),
        method: 'PUT',
      }),
  },
  attachmentRequirements: {
    list: (scope: 'vendor-application' | 'product', categoryCode = '') => {
      const path =
        scope === 'vendor-application'
          ? '/api/vendor-application-attachment-requirements'
          : '/api/product-attachment-requirements';
      const query = categoryCode ? `?category_code=${encodeURIComponent(categoryCode)}` : '';
      return request<{ requirements: AttachmentRequirement[] }>(`${path}${query}`);
    },
    update: (
      scope: 'vendor-application' | 'product',
      id: string,
      input: {
        label: string;
        sort_order: number;
        required: boolean;
        is_active: boolean;
      },
    ) => {
      const path =
        scope === 'vendor-application'
          ? '/api/vendor-application-attachment-requirements'
          : '/api/product-attachment-requirements';
      return request<{ requirement: AttachmentRequirement }>(`${path}/${id}`, {
        ...jsonBody(input),
        method: 'PUT',
      });
    },
  },
  productCategories: {
    publicList: () => request<{ categories: ProductCategory[] }>('/api/public/product-categories'),
    list: () => request<{ categories: ProductCategory[] }>('/api/product-categories'),
    update: (
      id: string,
      input: {
        name: string;
        sort_order: number;
        is_active: boolean;
        field_schema?: Record<string, unknown>;
      },
    ) =>
      request<{ category: ProductCategory }>(`/api/product-categories/${id}`, {
        ...jsonBody(input),
        method: 'PUT',
      }),
  },
  audit: () => request<{ logs: Record<string, unknown>[] }>('/api/audit-logs'),
  vendorProactive: {
    config: () =>
      request<{
        enabled: boolean;
        allow_new_vendors: boolean;
        require_email_verification: boolean;
        completeness_threshold: number;
        max_file_size_mb: number;
        max_file_count: number;
        allowed_file_types: string[];
        turnstile_site_key: string;
      }>('/api/public/vendor-proactive/config'),
    applicationAttachmentRequirements: () =>
      request<{ requirements: AttachmentRequirement[] }>(
        '/api/public/vendor-proactive/application-attachment-requirements',
      ),
    application: (input: unknown) =>
      request<{
        application_id: string;
        application_no?: string;
        reused?: boolean;
        direct_access?: boolean;
        draft?: boolean;
      }>('/api/public/vendor-proactive/applications', jsonBody(input)),
    applicationDetail: () => request<VendorApplication>('/api/public/vendor-proactive/application'),
    applicationUpdate: (input: unknown) =>
      request<VendorApplication>('/api/public/vendor-proactive/application', {
        ...jsonBody(input),
        method: 'PUT',
      }),
    applicationResubmit: (input: unknown) =>
      request<VendorApplication>('/api/public/vendor-proactive/application/resubmit', {
        ...jsonBody(input),
        method: 'POST',
      }),
    uploadApplicationAttachment: async (applicationId: string, type: string, file: File) => {
      const form = new FormData();
      form.set('application_id', applicationId);
      form.set('type', type);
      form.set('file', file);
      return request<unknown>('/api/public/vendor-proactive/application-attachments', {
        method: 'POST',
        body: form,
      });
    },
    applicationAttachmentDownloadUrl: (id: string) =>
      `/api/public/vendor-proactive/application-attachments/${id}`,
    requestVerification: (input: unknown) =>
      request<{
        challenge_id?: string;
        direct_access?: boolean;
        vendor_name?: string;
        expires_at: string;
        dev_code?: string;
        verification_url?: string;
      }>('/api/public/vendor-proactive/verification/request', jsonBody(input)),
    verify: (input: unknown) =>
      request<{ vendor_name: string; expires_at: string }>(
        '/api/public/vendor-proactive/verification/verify',
        jsonBody(input),
      ),
    session: () =>
      request<{
        vendor_name: string;
        tax_id: string;
        contact_email: string;
        vendor_id: string | null;
        application_id: string | null;
        application_status?: string | null;
        vendor_status?: string | null;
        account_id?: string | null;
        user_role?: string | null;
        skip_email_verification?: boolean;
      }>('/api/public/vendor-proactive/session'),
    logout: () => request<null>('/api/public/vendor-proactive/session', { method: 'DELETE' }),
    invitations: {
      accept: (token: string) =>
        request<{ invitation: VendorProposalInvitation; vendor_name: string; tax_id: string }>(
          `/api/public/vendor-proactive/invitations/accept/${encodeURIComponent(token)}`,
          { method: 'POST' },
        ),
      list: () =>
        request<{
          invitations: Array<
            VendorProposalInvitation & {
              fill_status: string;
              invitation_status: string;
              expires_at: string;
              revoked_at: string | null;
              proposal_count: number;
            }
          >;
        }>('/api/public/vendor-proactive/invitations'),
      detail: (id: string) =>
        request<{
          invitation: VendorProposalInvitation & {
            fill_status: string;
            invitation_status: string;
            expires_at: string;
            revoked_at: string | null;
            proposal_count: number;
          };
        }>(`/api/public/vendor-proactive/invitations/${id}`),
      start: (id: string) =>
        request<VendorProposal>(`/api/public/vendor-proactive/invitations/${id}/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        }),
    },
    proposals: {
      list: () =>
        request<{ proposals: VendorProposal[] }>('/api/public/vendor-proactive/proposals'),
      create: () => request<VendorProposal>('/api/public/vendor-proactive/proposals', jsonBody({})),
      detail: (id: string) =>
        request<VendorProposal>(`/api/public/vendor-proactive/proposals/${id}`),
      save: (id: string, input: unknown) =>
        request<VendorProposal>(`/api/public/vendor-proactive/proposals/${id}`, {
          ...jsonBody(input),
          method: 'PUT',
        }),
      submit: (
        id: string,
        turnstileToken: string,
        idempotencyKey?: string,
        quantifiedContentConfirmed = false,
      ) =>
        request<VendorProposal>(
          `/api/public/vendor-proactive/proposals/${id}/submit`,
          jsonBody({
            turnstile_token: turnstileToken,
            idempotency_key: idempotencyKey,
            quantified_content_confirmed: quantifiedContentConfirmed,
          }),
        ),
      copy: (id: string) =>
        request<VendorProposal>(`/api/public/vendor-proactive/proposals/${id}/copy`, jsonBody({})),
      remove: (id: string) =>
        request<{ id: string; deleted_at: string }>(
          `/api/public/vendor-proactive/proposals/${id}`,
          { method: 'DELETE' },
        ),
      versions: (id: string) =>
        request<{ versions: VendorProposalVersion[] }>(
          `/api/public/vendor-proactive/proposals/${id}/versions`,
        ),
      requestChange: (id: string, input: { reason: string; proposed: Record<string, unknown> }) =>
        request<VendorChangeRequest>(
          '/api/public/vendor-proactive/vendor-change-requests',
          jsonBody({ change_type: 'PROPOSAL', proposal_id: id, ...input }),
        ),
    },
    profile: {
      get: () =>
        request<{ profile: VendorSelfServiceProfile }>(
          '/api/public/vendor-proactive/vendor-profile',
        ),
      updateContact: (input: Record<string, unknown>) =>
        request<{ profile: VendorSelfServiceProfile }>(
          '/api/public/vendor-proactive/vendor-profile/contact',
          { ...jsonBody(input), method: 'PUT' },
        ),
      requestChange: (input: { reason: string; proposed: Record<string, unknown> }) =>
        request<VendorChangeRequest>(
          '/api/public/vendor-proactive/vendor-change-requests',
          jsonBody({ change_type: 'VENDOR_PROFILE', ...input }),
        ),
      attachmentUrl: (id: string, preview = false) =>
        `/api/public/vendor-proactive/vendor-profile/attachments/${id}${preview ? '?preview=1' : ''}`,
    },
    changeRequests: {
      list: () =>
        request<{ requests: VendorChangeRequest[] }>(
          '/api/public/vendor-proactive/vendor-change-requests',
        ),
    },
    upload: async (proposalId: string, type: string, file: File, barcodeValue?: string) => {
      const form = new FormData();
      form.set('proposal_id', proposalId);
      form.set('type', type);
      if (barcodeValue) form.set('barcode_value', barcodeValue);
      form.set('file', file);
      return request<VendorProposalAttachment>('/api/public/vendor-proactive/attachments', {
        method: 'POST',
        body: form,
      });
    },
    deleteAttachment: (proposalId: string, attachmentId: string) =>
      request<{ attachments: VendorProposalAttachment[]; deleted_at: string }>(
        `/api/public/vendor-proactive/proposals/${proposalId}/attachments/${attachmentId}`,
        { method: 'DELETE' },
      ),
    downloadUrl: (id: string, preview = false) =>
      `/api/public/vendor-proactive/attachments/${id}${preview ? '?preview=1' : ''}`,
  },
  vendorChangeRequests: {
    list: (status = '') =>
      request<{ requests: VendorChangeRequest[] }>(
        `/api/vendor-change-requests${status ? `?status=${encodeURIComponent(status)}` : ''}`,
      ),
    detail: (id: string) =>
      request<{ request: VendorChangeRequest }>(`/api/vendor-change-requests/${id}`).then(
        (result) => result.request,
      ),
    review: (id: string, input: { action: string; reason?: string }) =>
      request<VendorChangeRequest>(`/api/vendor-change-requests/${id}/review`, jsonBody(input)),
  },
  proactive: {
    list: (params: Record<string, string> = {}) =>
      request<{ proposals: VendorProposal[]; filters: Record<string, string> }>(
        `/api/vendor-proposals?${new URLSearchParams(params)}`,
      ),
    dashboard: () =>
      request<{ metrics: Record<string, number> }>('/api/vendor-proposals/dashboard'),
    detail: (id: string, includeDeleted = false) =>
      request<VendorProposal>(
        `/api/vendor-proposals/${id}${includeDeleted ? '?include_deleted=1' : ''}`,
      ),
    updateClassification: (id: string, input: unknown) =>
      request<VendorProposal>(`/api/vendor-proposals/${id}/classification`, jsonBody(input)),
    reviewTradeTerms: (
      id: string,
      input: { decision: 'ACCEPT' | 'REJECT' | 'REQUEST_REVISION'; reason?: string },
    ) => request<VendorProposal>(`/api/vendor-proposals/${id}/trade-terms`, jsonBody(input)),
    requestRevision: (id: string, input: unknown) =>
      request<VendorProposal>(`/api/vendor-proposals/${id}/request-revision`, jsonBody(input)),
    status: (id: string, input: unknown) =>
      request<VendorProposal>(`/api/vendor-proposals/${id}/status`, jsonBody(input)),
    createProductMaster: (id: string, input: unknown) =>
      request<VendorProposal>(`/api/vendor-proposals/${id}/product-master`, jsonBody(input)),
    reports: (params: Record<string, string> = {}) =>
      request<{
        rows: Record<string, unknown>[];
        category_stats: Record<string, unknown>[];
        filters: Record<string, string>;
        generated_at: string;
      }>(`/api/vendor-proposals/reports?${new URLSearchParams(params)}`),
    reportCsvUrl: (params: Record<string, string> = {}) =>
      `/api/vendor-proposals/reports?${new URLSearchParams({ ...params, format: 'csv' })}`,
  },
  productMasters: {
    list: (params: Record<string, string> = {}) =>
      request<{ items: FormalProductMaster[]; total: number; limit: number; offset: number }>(
        `/api/product-masters?${new URLSearchParams(params)}`,
      ),
    previewTxt: (content: string, fileName?: string, encoding?: string) =>
      request<{
        file_name: string;
        file_hash?: string;
        previous_import_warning?: string | null;
        encoding: string;
        total_rows: number;
        importable_rows: number;
        warning_rows: number;
        invalid_rows_count: number;
        invalid_rows: Array<{ rowNumber: number; itemId?: string; cname?: string; reason: string }>;
        breakdown: {
          inserted: number;
          updated: number;
          unchanged: number;
          price_changed: number;
          high_risk_price: number;
          identity_conflicts: number;
        };
        summary: Record<string, number>;
        header_columns: string[];
        preview_rows: Array<Record<string, any>>;
      }>(
        '/api/product-masters/txt/parse-preview',
        jsonBody({ content, file_name: fileName, encoding }),
      ),
    startImport: (input: {
      file_name: string;
      file_hash?: string;
      encoding?: string;
      tax_type?: 'TAX_INCLUSIVE' | 'TAX_EXCLUSIVE';
      total_rows: number;
      warning_rows: number;
      invalid_rows_count: number;
      summary?: any;
    }) =>
      request<{ batch_id: string; batch_no: string; tax_type: string }>(
        '/api/product-masters/txt/import-start',
        jsonBody(input),
      ),
    importChunk: (input: {
      batch_id: string;
      tax_type?: 'TAX_INCLUSIVE' | 'TAX_EXCLUSIVE';
      chunk: any[];
    }) =>
      request<{
        inserted_count: number;
        updated_count: number;
        unchanged_count: number;
        price_change_count: number;
      }>('/api/product-masters/txt/import-chunk', jsonBody(input)),
    finishImport: (input: {
      batch_id: string;
      inserted_count: number;
      updated_count: number;
      unchanged_count: number;
      price_change_count: number;
      all_imported_codes?: string[];
      sample_for_duplicates?: any[];
    }) =>
      request<{
        success: boolean;
        batch_id: string;
        inserted_rows: number;
        updated_rows: number;
        unchanged_rows: number;
        duplicate_candidate_count: number;
      }>('/api/product-masters/txt/import-finish', jsonBody(input)),
    commitTxt: (
      content: string,
      fileName?: string,
      encoding?: string,
      taxType: 'TAX_INCLUSIVE' | 'TAX_EXCLUSIVE' = 'TAX_INCLUSIVE',
    ) =>
      request<{
        success: boolean;
        batch_id: string;
        batch_no: string;
        tax_type: string;
        total_rows: number;
        importable_rows: number;
        inserted_rows: number;
        updated_rows: number;
        unchanged_rows: number;
        warning_rows: number;
        invalid_rows_count: number;
        invalid_rows: Array<{ rowNumber: number; itemId?: string; cname?: string; reason: string }>;
        price_change_count: number;
        duplicate_candidate_count: number;
      }>(
        '/api/product-masters/txt/import-commit',
        jsonBody({ content, file_name: fileName, encoding, tax_type: taxType }),
      ),
    getDataQualityTabs: (tab = 'price_changes') =>
      request<{
        summary: Record<string, number>;
        items: any[];
      }>(`/api/product-masters/data-quality/tabs?tab=${encodeURIComponent(tab)}`),
    batchResolveQuality: (tab: string, action: string, ids: string[], notes?: string) =>
      request<{ success: boolean; resolved_count: number }>(
        '/api/product-masters/data-quality/batch-resolve',
        jsonBody({ tab, action, ids, notes }),
      ),
    rollbackBatch: (batchId: string) =>
      request<{ success: boolean; message: string }>(
        `/api/product-masters/import-batches/${batchId}/rollback`,
        { method: 'POST' },
      ),
    resolveDuplicate: (candidateId: string, action: string, resolutionNotes?: string) =>
      request<{ success: boolean; message: string }>(
        '/api/product-masters/duplicate-resolution',
        jsonBody({ candidate_id: candidateId, action, resolution_notes: resolutionNotes }),
      ),
    resolvePriceReview: (
      productId: string,
      decision: 'ACCEPT_NEW_PRICE' | 'RETAIN_OLD_PRICE',
      newPrice?: number,
      notes?: string,
    ) =>
      request<{ success: boolean; message: string }>(
        '/api/product-masters/price-review-resolution',
        jsonBody({ product_id: productId, decision, new_price: newPrice, notes }),
      ),
    updateCustomFields: (
      id: string,
      input: {
        current_vendor_giveaway_terms?: string;
        ingredients_json?: string;
        product_family_id?: string | null;
        product_category_code?: string | null;
        product_subcategory_code?: string | null;
        record_type?: string;
      },
    ) =>
      request<{ success: boolean; message: string }>(`/api/product-masters/${id}/custom-fields`, {
        method: 'PUT',
        ...jsonBody(input),
      }),
    qualityCenter: () =>
      request<{ summary: Record<string, number> }>('/api/product-masters/quality-center'),
    importBatches: () =>
      request<{ items: ImportBatchRecord[] }>('/api/product-masters/import-batches'),
  },
  productFamilies: {
    list: () => request<{ items: ProductFamilyItem[] }>('/api/product-families'),
    create: (input: {
      family_name: string;
      family_code?: string;
      brand_name?: string;
      description?: string;
    }) =>
      request<{ success: boolean; family_id: string }>(
        '/api/product-families',
        jsonBody({ action: 'CREATE', ...input }),
      ),
    addMember: (
      familyId: string,
      productId: string,
      packageSpec?: string,
      unitMultiplier?: number,
    ) =>
      request<{ success: boolean }>(
        '/api/product-families',
        jsonBody({
          action: 'ADD_MEMBER',
          family_id: familyId,
          product_id: productId,
          package_spec: packageSpec,
          unit_multiplier: unitMultiplier,
        }),
      ),
    removeMember: (familyId: string, productId: string) =>
      request<{ success: boolean }>(
        '/api/product-families',
        jsonBody({ action: 'REMOVE_MEMBER', family_id: familyId, product_id: productId }),
      ),
  },
  productMaster: {
    search: async (params: Record<string, string> = {}) => {
      const res = await request<{
        success: boolean;
        items: Array<any>;
        total: number;
        limit: number;
        offset: number;
      }>(`/api/product-master/search?${new URLSearchParams(params)}`);
      return {
        ...res,
        items: (res.items || []).map(mapProductMasterRow),
      };
    },
  },
  competitors: {
    list: (proposalId: string) =>
      request<{
        success: boolean;
        proposalId: string;
        competitors: Array<any>;
        primaryCompetitor: any | null;
      }>(`/api/proposals/${proposalId}/competitors`),
    add: (proposalId: string, productId: string, source = 'INTERNAL_PRODUCT_MASTER') =>
      request<{
        success: boolean;
        alreadyExists: boolean;
        message: string;
        competitor: any;
      }>(`/api/proposals/${proposalId}/competitors`, {
        method: 'POST',
        ...jsonBody({ productId, source }),
      }),
    setPrimary: (proposalId: string, productId: string) =>
      request<{
        success: boolean;
        message: string;
        primaryCompetitor: any;
        price_summary: string;
      }>(`/api/proposals/${proposalId}/primary-competitor`, {
        method: 'PUT',
        ...jsonBody({ productId }),
      }),
    remove: (proposalId: string, competitorId: string) =>
      request<{ success: boolean; message: string }>(
        `/api/proposals/${proposalId}/competitors/${competitorId}`,
        { method: 'DELETE' },
      ),
    reorder: (proposalId: string, competitorIds: string[]) =>
      request<{ success: boolean; message: string }>(
        `/api/proposals/${proposalId}/competitors/order`,
        {
          method: 'PATCH',
          ...jsonBody({ competitor_ids: competitorIds }),
        },
      ),
  },
  competitorComparisons: {
    get: (proposalId: string) =>
      request<CompetitorComparisonData>(`/api/competitor-comparisons/${proposalId}`),
    autoPrepare: (proposalId: string) =>
      request<{
        success: boolean;
        message: string;
        primary_competitor_id?: string;
        top_candidates: Array<any>;
        price_summary: string;
      }>(`/api/competitor-comparisons/${proposalId}/auto-prepare`, { method: 'POST' }),
    searchCandidates: (proposalId: string) =>
      request<{ items: Array<any> }>(
        `/api/competitor-comparisons/${proposalId}/search-candidates`,
        { method: 'POST' },
      ),
    setPrimary: (proposalId: string, productId: string) =>
      request<{
        success: boolean;
        message: string;
        primaryCompetitor: any;
        price_summary: string;
      }>(`/api/proposals/${proposalId}/primary-competitor`, {
        method: 'PUT',
        ...jsonBody({ productId }),
      }),
    reorder: (proposalId: string, competitorIds: string[]) =>
      request<{ success: boolean; message: string }>(
        `/api/proposals/${proposalId}/competitors/order`,
        {
          method: 'PATCH',
          ...jsonBody({ competitor_ids: competitorIds }),
        },
      ),
    updateStatus: (
      proposalId: string,
      input: { primary_competitor_product_id?: string; selected_competitor_ids?: string[] },
    ) =>
      request<{ success: boolean; message?: string }>(
        `/api/proposals/${proposalId}/primary-competitor`,
        {
          method: 'PUT',
          ...jsonBody({ productId: input.primary_competitor_product_id }),
        },
      ),
    confirmWebPrice: (
      observationId: string,
      input: { confirmed: boolean; exclusion_reason?: string },
    ) =>
      request<{ success: boolean }>(
        `/api/competitor-comparisons/web-prices/${observationId}/confirm`,
        {
          method: 'PUT',
          ...jsonBody(input),
        },
      ),
    saveConclusion: (
      proposalId: string,
      input: {
        buyer_advantages?: string;
        buyer_disadvantages?: string;
        is_price_competitive?: boolean;
        is_ingredient_superior?: boolean;
        is_giveaway_superior?: boolean;
        is_margin_superior?: boolean;
        replacement_assessment?: string;
        buyer_conclusion_notes?: string;
        return_risk_notes?: string;
        inventory_replacement_risk_notes?: string;
        strategy_selection?: any;
      },
    ) =>
      request<{ success: boolean; message: string }>(
        `/api/competitor-comparisons/${proposalId}/conclusion`,
        {
          method: 'PUT',
          ...jsonBody(input),
        },
      ),
    createSnapshot: (proposalId: string) =>
      request<{ success: boolean; snapshot_id: string; message: string }>(
        `/api/competitor-comparisons/${proposalId}/create-snapshot`,
        { method: 'POST' },
      ),
  },
  productEnrichment: {
    getSummary: () =>
      request<{
        success: boolean;
        summary: {
          total: number;
          notSearched: number;
          searching: number;
          foundUnconfirmed: number;
          confirmed: number;
          notFound: number;
          ambiguous: number;
          identityUnconfirmed: number;
          missingIngredients: number;
        };
      }>('/api/product-enrichment/summary'),
    enrichSingle: (productId: string) =>
      request<{
        success: boolean;
        status: string;
        message: string;
        candidates: Array<any>;
        matchedCandidate?: any;
      }>(`/api/products/${productId}/enrichment/ingredients`, { method: 'POST' }),
    enrichBatch: (params: { limit?: number; categoryCode?: string; forceAll?: boolean } = {}) =>
      request<{
        success: boolean;
        message?: string;
        totalProcessed: number;
        enrichedCount: number;
        notFoundCount: number;
        items: Array<{
          id: string;
          productCode: string;
          productName: string;
          status: string;
          score: number;
          sourceName: string;
        }>;
      }>('/api/product-enrichment/ingredients/batch', {
        method: 'POST',
        ...jsonBody(params),
      }),
    getCandidates: (productId: string) =>
      request<{
        success: boolean;
        product: any;
        sources: Array<any>;
        ingredients: Array<any>;
      }>(`/api/products/${productId}/ingredient-candidates`),
    confirm: (
      productId: string,
      input: {
        sourceId?: string;
        rawText?: string;
        ingredients?: Array<{
          nameRaw: string;
          nameNormalized?: string;
          amount?: number | null;
          unit?: string;
          sequence?: number;
        }>;
      },
    ) =>
      request<{ success: boolean; message: string }>(
        `/api/products/${productId}/ingredients/confirm`,
        {
          method: 'POST',
          ...jsonBody(input),
        },
      ),
    rejectSource: (productId: string, sourceId: string, reason?: string) =>
      request<{ success: boolean; message: string }>(
        `/api/products/${productId}/ingredients/reject-source`,
        {
          method: 'POST',
          ...jsonBody({ sourceId, reason }),
        },
      ),
  },
};

export async function downloadBlob(url: string): Promise<Blob> {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok)
    throw new ApiError(
      'DOWNLOAD_FAILED',
      '匯出失敗。',
      response.status,
      undefined,
      response.headers.get('X-Request-Id') ?? undefined,
    );
  return response.blob();
}
